/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-namespace, no-inner-declarations */
import L from "leaflet";
import Labelgun from "labelgun/lib/labelgun";
import { MARKER_HOVER_DELAY_MS } from "../markerHoverDelay";

// Type definitions for PruneCluster
interface Position {
  lat: number;
  lng: number;
}

interface Bounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

interface MarkerData {
  [key: string]: unknown;
  icon?: L.Icon | ((data: MarkerData, category?: number) => L.Icon);
  popup?: string | ((data: MarkerData, category?: number) => string);
  popupOptions?: L.PopupOptions;
  forceIconRedraw?: boolean;
}

interface LabelgunLabel {
  id: string;
  labelObject: HTMLElement;
}

interface OverlappingMarkersEvent {
  cluster: PruneClusterForLeaflet;
  markers: PruneCluster.Marker[];
  center: L.LatLng;
  marker: L.Marker;
}

interface ClusterHoverEvent {
  cluster: PruneClusterForLeaflet;
  markers: PruneCluster.Marker[];
  center: L.LatLng;
  marker: L.Marker;
  oversized: boolean;
}

interface ClusterUnhoverEvent {
  marker: L.Marker;
}

interface ClusterOversizedEvent {
  cluster: PruneClusterForLeaflet;
  markers: PruneCluster.Marker[];
  center: L.LatLng;
  marker: L.Marker;
}

// Extend Leaflet types
declare module "leaflet" {
  namespace Map {
    interface MapEventMap {
      overlappingmarkers: OverlappingMarkersEvent;
      clusterhover: ClusterHoverEvent;
      clusterunhover: ClusterUnhoverEvent;
      clusteroversized: ClusterOversizedEvent;
    }
  }

  interface Marker {
    _leafletClusterBounds?: Bounds;
    _removeFromMap?: boolean;
    _zoomLevel?: number;
    _hashCode?: number;
    _population?: number;
  }
}

namespace PruneCluster {
  export abstract class ClusterObject {
    public hashCode: number = 1;
    public data: MarkerData = {};
    public position: Position = { lat: 0, lng: 0 };
  }

  export class Point extends ClusterObject {}

  let hashCodeCounter = 1;
  const maxHashCodeValue = Math.pow(2, 53) - 1;

  export class Marker extends ClusterObject {
    public weight: number;
    public category?: number;
    public filtered: boolean;
    public _removeFlag?: boolean;

    constructor(
      lat: number,
      lng: number,
      data: MarkerData = {},
      category?: number,
      weight: number = 1,
      filtered: boolean = false
    ) {
      super();
      this.data = data;
      this.position = { lat: +lat, lng: +lng };
      this.weight = weight;
      this.category = category;
      this.filtered = filtered;
      this.hashCode = hashCodeCounter++;
    }

    public Move(lat: number, lng: number): void {
      this.position.lat = +lat;
      this.position.lng = +lng;
    }

    public SetData(data: Partial<MarkerData>): void {
      for (const key in data) {
        this.data[key] = data[key];
      }
    }
  }

  export class Cluster extends ClusterObject {
    public static ENABLE_MARKERS_LIST = false;

    public stats: number[] = [0, 0, 0, 0, 0, 0, 0, 0];
    public lastMarker?: Marker;
    public population?: number;
    public totalWeight?: number;
    public averagePosition?: Position;
    public bounds?: Bounds;
    public _clusterMarkers?: Marker[];
    public _leafletCollision?: boolean;
    public _leafletOldPopulation?: number;
    public _leafletOldHashCode?: number;
    public _leafletMarker?: L.Marker;
    public _leafletPosition?: L.LatLng;

    constructor(marker?: Marker) {
      super();
      this.stats = [0, 0, 0, 0, 0, 0, 0, 0];
      this.data = {};

      if (!marker) {
        this.hashCode = 1;
        if (Cluster.ENABLE_MARKERS_LIST) {
          this._clusterMarkers = [];
        }
        return;
      }

      if (Cluster.ENABLE_MARKERS_LIST) {
        this._clusterMarkers = [marker];
      }

      this.lastMarker = marker;
      this.hashCode = 31 + marker.hashCode;
      this.population = 1;

      if (marker.category !== undefined) {
        this.stats[marker.category] = 1;
      }

      this.totalWeight = marker.weight;
      this.position = {
        lat: marker.position.lat,
        lng: marker.position.lng,
      };
      this.averagePosition = {
        lat: marker.position.lat,
        lng: marker.position.lng,
      };
    }

    public AddMarker(marker: Marker): void {
      if (Cluster.ENABLE_MARKERS_LIST && this._clusterMarkers) {
        this._clusterMarkers.push(marker);
      }

      let h = this.hashCode;
      h = (h << 5) - h + marker.hashCode;
      if (h >= maxHashCodeValue) {
        this.hashCode = h % maxHashCodeValue;
      } else {
        this.hashCode = h;
      }

      this.lastMarker = marker;
      const weight = marker.weight;
      const currentTotalWeight = this.totalWeight || 0;
      const newWeight = weight + currentTotalWeight;

      if (this.averagePosition) {
        this.averagePosition.lat =
          (this.averagePosition.lat * currentTotalWeight +
            marker.position.lat * weight) /
          newWeight;
        this.averagePosition.lng =
          (this.averagePosition.lng * currentTotalWeight +
            marker.position.lng * weight) /
          newWeight;
      }

      this.population = (this.population || 0) + 1;
      this.totalWeight = newWeight;

      if (marker.category !== undefined) {
        this.stats[marker.category] = this.stats[marker.category] + 1 || 1;
      }
    }

    public Reset(): void {
      this.hashCode = 1;
      this.lastMarker = undefined;
      this.population = 0;
      this.totalWeight = 0;
      this.stats = [0, 0, 0, 0, 0, 0, 0, 0];
      if (Cluster.ENABLE_MARKERS_LIST) {
        this._clusterMarkers = [];
      }
    }

    public ComputeBounds(cluster: PruneCluster): void {
      const proj = cluster.Project(this.position.lat, this.position.lng);
      const size = cluster.Size;
      const nbX = Math.floor(proj.x / size);
      const nbY = Math.floor(proj.y / size);
      const startX = nbX * size;
      const startY = nbY * size;

      const a = cluster.UnProject(startX, startY);
      const b = cluster.UnProject(startX + size, startY + size);

      this.bounds = {
        minLat: b.lat,
        maxLat: a.lat,
        minLng: a.lng,
        maxLng: b.lng,
      };
    }

    public GetClusterMarkers(): Marker[] {
      return this._clusterMarkers || [];
    }

    public ApplyCluster(newCluster: Cluster): void {
      this.hashCode = this.hashCode * 41 + newCluster.hashCode * 43;
      if (this.hashCode > maxHashCodeValue) {
        this.hashCode = maxHashCodeValue;
      }

      const weight = newCluster.totalWeight || 0;
      const currentTotalWeight = this.totalWeight || 0;
      const newWeight = weight + currentTotalWeight;

      if (this.averagePosition && newCluster.averagePosition) {
        this.averagePosition.lat =
          (this.averagePosition.lat * currentTotalWeight +
            newCluster.averagePosition.lat * weight) /
          newWeight;
        this.averagePosition.lng =
          (this.averagePosition.lng * currentTotalWeight +
            newCluster.averagePosition.lng * weight) /
          newWeight;
      }

      this.population = (this.population || 0) + (newCluster.population || 0);
      this.totalWeight = newWeight;

      if (this.bounds && newCluster.bounds) {
        this.bounds.minLat = Math.min(
          this.bounds.minLat,
          newCluster.bounds.minLat
        );
        this.bounds.minLng = Math.min(
          this.bounds.minLng,
          newCluster.bounds.minLng
        );
        this.bounds.maxLat = Math.max(
          this.bounds.maxLat,
          newCluster.bounds.maxLat
        );
        this.bounds.maxLng = Math.max(
          this.bounds.maxLng,
          newCluster.bounds.maxLng
        );
      }

      for (const category in newCluster.stats) {
        if (Object.prototype.hasOwnProperty.call(newCluster.stats, category)) {
          const categoryIndex = parseInt(category, 10);
          if (Object.prototype.hasOwnProperty.call(this.stats, categoryIndex)) {
            this.stats[categoryIndex] += newCluster.stats[categoryIndex];
          } else {
            this.stats[categoryIndex] = newCluster.stats[categoryIndex];
          }
        }
      }

      if (Cluster.ENABLE_MARKERS_LIST && this._clusterMarkers) {
        this._clusterMarkers = this._clusterMarkers.concat(
          newCluster.GetClusterMarkers()
        );
      }
    }
  }

  function checkPositionInsideBounds(a: Position, b: Bounds): boolean {
    return (
      a.lat >= b.minLat &&
      a.lat <= b.maxLat &&
      a.lng >= b.minLng &&
      a.lng <= b.maxLng
    );
  }

  function insertionSort(list: ClusterObject[]): void {
    for (let i = 1, j: number, tmp: ClusterObject, tmpLng: number, length = list.length; i < length; ++i) {
      tmp = list[i];
      tmpLng = tmp.position.lng;
      for (j = i - 1; j >= 0 && list[j].position.lng > tmpLng; --j) {
        list[j + 1] = list[j];
      }
      list[j + 1] = tmp;
    }
  }

  function shouldUseInsertionSort(total: number, nbChanges: number): boolean {
    if (nbChanges > 300) {
      return false;
    } else {
      return nbChanges / total < 0.2;
    }
  }

  export class PruneCluster {
    public Size = 166;
    public ViewPadding = 0.2;

    private _markers: Marker[] = [];
    private _nbChanges = 0;
    private _clusters: Cluster[] = [];

    public Project!: (lat: number, lng: number) => L.Point;
    public UnProject!: (x: number, y: number) => L.LatLng;

    public RegisterMarker(marker: Marker): void {
      if (marker._removeFlag) {
        delete marker._removeFlag;
      }
      this._markers.push(marker);
      this._nbChanges += 1;
    }

    public RegisterMarkers(markers: Marker[]): void {
      markers.forEach((marker) => {
        this.RegisterMarker(marker);
      });
    }

    private _sortMarkers(): void {
      const markers = this._markers;
      const length = markers.length;
      if (this._nbChanges && !shouldUseInsertionSort(length, this._nbChanges)) {
        this._markers.sort((a, b) => a.position.lng - b.position.lng);
      } else {
        insertionSort(markers);
      }
      this._nbChanges = 0;
    }

    private _sortClusters(): void {
      insertionSort(this._clusters);
    }

    private _indexLowerBoundLng(lng: number): number {
      const markers = this._markers;
      let it: number;
      let step: number;
      let first = 0;
      let count = markers.length;

      while (count > 0) {
        step = Math.floor(count / 2);
        it = first + step;
        if (markers[it].position.lng < lng) {
          first = ++it;
          count -= step + 1;
        } else {
          count = step;
        }
      }
      return first;
    }

    private _resetClusterViews(): void {
      for (let i = 0, l = this._clusters.length; i < l; ++i) {
        const cluster = this._clusters[i];
        cluster.Reset();
        cluster.ComputeBounds(this);
      }
    }

    public ProcessView(bounds: Bounds): Cluster[] {
      const heightBuffer =
        Math.abs(bounds.maxLat - bounds.minLat) * this.ViewPadding;
      const widthBuffer =
        Math.abs(bounds.maxLng - bounds.minLng) * this.ViewPadding;

      const extendedBounds = {
        minLat: bounds.minLat - heightBuffer - heightBuffer,
        maxLat: bounds.maxLat + heightBuffer + heightBuffer,
        minLng: bounds.minLng - widthBuffer - widthBuffer,
        maxLng: bounds.maxLng + widthBuffer + widthBuffer,
      };

      this._sortMarkers();
      this._resetClusterViews();

      const firstIndex = this._indexLowerBoundLng(extendedBounds.minLng);
      const markers = this._markers;
      const clusters = this._clusters;
      const workingClusterList = clusters.slice(0);

      for (let i = firstIndex, l = markers.length; i < l; ++i) {
        const marker = markers[i];
        const markerPosition = marker.position;

        if (markerPosition.lng > extendedBounds.maxLng) {
          break;
        }

        if (
          markerPosition.lat > extendedBounds.minLat &&
          markerPosition.lat < extendedBounds.maxLat &&
          !marker.filtered
        ) {
          let clusterFound = false;
          let cluster: Cluster;

          for (let j = 0, ll = workingClusterList.length; j < ll; ++j) {
            cluster = workingClusterList[j];
            if (cluster.bounds && cluster.bounds.maxLng < marker.position.lng) {
              workingClusterList.splice(j, 1);
              --j;
              --ll;
              continue;
            }
            if (cluster.bounds && checkPositionInsideBounds(markerPosition, cluster.bounds)) {
              cluster.AddMarker(marker);
              clusterFound = true;
              break;
            }
          }

          if (!clusterFound) {
            cluster = new Cluster(marker);
            cluster.ComputeBounds(this);
            clusters.push(cluster);
            workingClusterList.push(cluster);
          }
        }
      }

      const newClustersList: Cluster[] = [];
      for (let i = 0, l = clusters.length; i < l; ++i) {
        const cluster = clusters[i];
        if ((cluster.population || 0) > 0) {
          newClustersList.push(cluster);
        }
      }

      this._clusters = newClustersList;
      this._sortClusters();
      return this._clusters;
    }

    public RemoveMarkers(markers?: Marker[]): void {
      if (!markers) {
        this._markers = [];
        return;
      }

      for (let i = 0, l = markers.length; i < l; ++i) {
        markers[i]._removeFlag = true;
      }

      const newMarkersList: Marker[] = [];
      for (let i = 0, l = this._markers.length; i < l; ++i) {
        if (!this._markers[i]._removeFlag) {
          newMarkersList.push(this._markers[i]);
        } else {
          delete this._markers[i]._removeFlag;
        }
      }
      this._markers = newMarkersList;
    }

    public FindMarkersInArea(area: Bounds): Marker[] {
      const aMinLat = area.minLat;
      const aMaxLat = area.maxLat;
      const aMinLng = area.minLng;
      const aMaxLng = area.maxLng;
      const markers = this._markers;
      const result: Marker[] = [];

      const firstIndex = this._indexLowerBoundLng(aMinLng);
      for (let i = firstIndex, l = markers.length; i < l; ++i) {
        const pos = markers[i].position;
        if (pos.lng > aMaxLng) {
          break;
        }
        if (pos.lat >= aMinLat && pos.lat <= aMaxLat && pos.lng >= aMinLng) {
          result.push(markers[i]);
        }
      }
      return result;
    }

    public ComputeBounds(markers: Marker[], withFiltered: boolean = true): Bounds | null {
      if (!markers || !markers.length) {
        return null;
      }

      let rMinLat = Number.MAX_VALUE;
      let rMaxLat = -Number.MAX_VALUE;
      let rMinLng = Number.MAX_VALUE;
      let rMaxLng = -Number.MAX_VALUE;

      for (let i = 0, l = markers.length; i < l; ++i) {
        if (!withFiltered && markers[i].filtered) {
          continue;
        }
        const pos = markers[i].position;
        if (pos.lat < rMinLat) rMinLat = pos.lat;
        if (pos.lat > rMaxLat) rMaxLat = pos.lat;
        if (pos.lng < rMinLng) rMinLng = pos.lng;
        if (pos.lng > rMaxLng) rMaxLng = pos.lng;
      }

      return {
        minLat: rMinLat,
        maxLat: rMaxLat,
        minLng: rMinLng,
        maxLng: rMaxLng,
      };
    }

    public FindMarkersBoundsInArea(area: Bounds): Bounds | null {
      return this.ComputeBounds(this.FindMarkersInArea(area));
    }

    public ComputeGlobalBounds(withFiltered: boolean = true): Bounds | null {
      return this.ComputeBounds(this._markers, withFiltered);
    }

    public GetMarkers(): Marker[] {
      return this._markers;
    }

    public GetPopulation(): number {
      return this._markers.length;
    }

    public ResetClusters(): void {
      this._clusters = [];
    }
  }
}


// Cluster icons must always stack above the single markers they collapse. They
// share a pane with those markers, and Leaflet orders a pane by latitude, so a
// static z-index offset is the only thing that keeps clusters on top. Spiderfied
// markers use SPIDERFY_Z_INDEX_OFFSET (higher) so they fan out above the cluster.
const CLUSTER_Z_INDEX_OFFSET = 10000;
const SPIDERFY_Z_INDEX_OFFSET = 20000;

class PruneClusterForLeaflet extends L.Layer {
  public Cluster: PruneCluster.PruneCluster;
  public clusterMargin: number;
  public spiderfier: PruneClusterLeafletSpiderfier;
  public options: { pane?: string } = {};
  public panelThreshold: number = Infinity;
  private _objectsOnMap: PruneCluster.Cluster[] = [];
  private _hardMove = false;
  private _resetIcons = false;
  private _removeTimeoutId = 0;
  private _markersRemoveListTimeout: L.Marker[] = [];
  private _labelGun: Labelgun;
  protected _map!: L.Map;
  private _moveInProgress = false;
  private _zoomInProgress = false;

  constructor(size: number = 120, clusterMargin: number = 20) {
    super();
    this.Cluster = new PruneCluster.PruneCluster();
    this.Cluster.Size = size;
    this.clusterMargin = Math.min(clusterMargin, size / 4);

    this.Cluster.Project = (lat: number, lng: number): L.Point => {
      return this._map.project(
        new L.LatLng(lat, lng),
        Math.floor(this._map.getZoom())
      );
    };

    this.Cluster.UnProject = (x: number, y: number): L.LatLng => {
      return this._map.unproject(
        new L.Point(x, y),
        Math.floor(this._map.getZoom())
      );
    };

    this._objectsOnMap = [];
    this.spiderfier = new PruneClusterLeafletSpiderfier(this);
    this._hardMove = false;
    this._resetIcons = false;
    this._removeTimeoutId = 0;
    this._markersRemoveListTimeout = [];
    this._labelGun = new Labelgun();
    this._labelGun.hideLabel = function(label: LabelgunLabel) {
      label.labelObject.style.opacity = "0";
    };
    this._labelGun.showLabel = function(label: LabelgunLabel) {
      label.labelObject.style.opacity = "1";
    };
  }

  public RegisterMarker(marker: PruneCluster.Marker): void {
    this.Cluster.RegisterMarker(marker);
  }

  public RegisterMarkers(markers: PruneCluster.Marker[]): void {
    this.Cluster.RegisterMarkers(markers);
  }

  public RemoveMarkers(markers?: PruneCluster.Marker[]): void {
    this.Cluster.RemoveMarkers(markers);
  }

  public BuildLeafletCluster(cluster: PruneCluster.Cluster, position: L.LatLng): L.Marker {
    const markerOptions: L.MarkerOptions = {
      icon: this.BuildLeafletClusterIcon(cluster),
      // Cluster icons and this layer's individual markers share one pane, and
      // within a pane Leaflet stacks markers by latitude (lower on screen wins).
      // That let individual icons cover cluster icons. Lift clusters with a
      // large z-index offset so a cluster always sits above the single markers
      // in the same pane. Spiderfied markers use a higher offset (see the
      // spiderfier) so they still fan out on top of the cluster.
      zIndexOffset: CLUSTER_Z_INDEX_OFFSET,
    };
    // Keep cluster icons in the same pane as this layer's individual markers so
    // the whole layer sits at one z-level relative to other layers. Without
    // this, cluster icons fall back to the default markerPane and float above
    // layers that are meant to sit below the always-visible top layer.
    if (this.options.pane) {
      markerOptions.pane = this.options.pane;
    }
    const m = new L.Marker(position, markerOptions);

    (m as any)._leafletClusterBounds = cluster.bounds;

    let hoverTimeout: ReturnType<typeof setTimeout> | undefined;
    const cancelPendingHover = () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = undefined;
      }
    };

    m.on("mouseover", () => {
      cancelPendingHover();
      hoverTimeout = setTimeout(() => {
        hoverTimeout = undefined;
        const cbounds = (m as any)._leafletClusterBounds;
        if (!cbounds) return;
        const markersArea = this.Cluster.FindMarkersInArea(cbounds as unknown as Bounds);
        // A cluster is "oversized" (worth showing the too-many-to-spider
        // message) when it can't be broken up by further zooming AND has at
        // least `panelThreshold` markers — same rule the click handler uses.
        let oversized = false;
        if (markersArea.length >= this.panelThreshold) {
          const b = this.Cluster.ComputeBounds(markersArea);
          if (b) {
            const bounds = new L.LatLngBounds(
              new L.LatLng(b.minLat, b.maxLng),
              new L.LatLng(b.maxLat, b.minLng)
            );
            const zoomBefore = this._map.getZoom();
            const zoomAfter = this._map.getBoundsZoom(
              bounds,
              false,
              new L.Point(20, 20)
            );
            oversized = zoomBefore === zoomAfter;
          }
        }
        this._map.fire("clusterhover", {
          cluster: this,
          markers: markersArea,
          center: m.getLatLng(),
          marker: m,
          oversized,
        });
      }, MARKER_HOVER_DELAY_MS);
    });

    m.on("mouseout", () => {
      cancelPendingHover();
      this._map.fire("clusterunhover", { marker: m });
    });

    m.on("click", () => {
      cancelPendingHover();
      const cbounds = (m as any)._leafletClusterBounds;
      if (!cbounds) return;

      const markersArea = this.Cluster.FindMarkersInArea(cbounds as unknown as Bounds);
      const b = this.Cluster.ComputeBounds(markersArea);
      if (b) {
        const bounds = new L.LatLngBounds(
          new L.LatLng(b.minLat, b.maxLng),
          new L.LatLng(b.maxLat, b.minLng)
        );
        const zoomLevelBefore = this._map.getZoom();
        const zoomLevelAfter = this._map.getBoundsZoom(
          bounds,
          false,
          new L.Point(20, 20)
        );

        if (zoomLevelBefore === zoomLevelAfter) {
          const filteredBounds: Bounds[] = [];
          for (let i = 0, l = this._objectsOnMap.length; i < l; ++i) {
            const o = this._objectsOnMap[i];
            if (o.data._leafletMarker !== m && o.bounds) {
              if (
                (o.bounds as unknown as Bounds).minLat >= (cbounds as unknown as Bounds).minLat &&
                (o.bounds as unknown as Bounds).maxLat <= (cbounds as unknown as Bounds).maxLat &&
                (o.bounds as unknown as Bounds).minLng >= (cbounds as unknown as Bounds).minLng &&
                (o.bounds as unknown as Bounds).maxLng <= (cbounds as unknown as Bounds).maxLng
              ) {
                filteredBounds.push(o.bounds);
              }
            }
          }

          if (filteredBounds.length > 0) {
            const newMarkersArea: PruneCluster.Marker[] = [];
            const ll = filteredBounds.length;
            for (let i = 0, l = markersArea.length; i < l; ++i) {
              const markerPos = markersArea[i].position;
              let isFiltered = false;
              for (let j = 0; j < ll; ++j) {
                const currentFilteredBounds = filteredBounds[j];
                if (
                  markerPos.lat >= currentFilteredBounds.minLat &&
                  markerPos.lat <= currentFilteredBounds.maxLat &&
                  markerPos.lng >= currentFilteredBounds.minLng &&
                  markerPos.lng <= currentFilteredBounds.maxLng
                ) {
                  isFiltered = true;
                  break;
                }
              }
              if (!isFiltered) {
                newMarkersArea.push(markersArea[i]);
              }
            }
            markersArea.splice(0, markersArea.length, ...newMarkersArea);
          }

          // Cluster can't be broken up further by zooming — decide between
          // "oversized" popup (large clusters), spider (small clusters), or
          // one more zoom step (extreme density but not at max zoom).
          if (markersArea.length >= this.panelThreshold) {
            this._map.fire("clusteroversized", {
              cluster: this,
              markers: markersArea,
              center: m.getLatLng(),
              marker: m,
            });
          } else if (
            markersArea.length < 200 ||
            zoomLevelAfter >= this._map.getMaxZoom()
          ) {
            this._map.fire("overlappingmarkers", {
              cluster: this,
              markers: markersArea,
              center: m.getLatLng(),
              marker: m,
            });
          } else {
            this._map.setView(m.getLatLng(), zoomLevelAfter + 1);
          }
        } else {
          this._map.fitBounds(bounds);
        }
      }
    });

    return m;
  }

  public BuildLeafletClusterIcon(cluster: PruneCluster.Cluster): L.DivIcon {
    let c = "prunecluster prunecluster-";
    let iconSize = 38;
    const maxPopulation = this.Cluster.GetPopulation();
    const population = cluster.population || 0;

    if (population < Math.max(10, maxPopulation * 0.01)) {
      c += "small";
    } else if (population < Math.max(100, maxPopulation * 0.05)) {
      c += "medium";
      iconSize = 40;
    } else {
      c += "large";
      iconSize = 44;
    }

    return new L.DivIcon({
      html: `<div><span>${population}</span></div>`,
      className: c,
      iconSize: L.point(iconSize, iconSize),
    });
  }

  public BuildLeafletMarker(marker: PruneCluster.Marker, position: L.LatLng): L.Marker {
    const m = new L.Marker(position);
    this.PrepareLeafletMarker(m, marker.data, marker.category);
    return m;
  }

  public PrepareLeafletMarker(marker: L.Marker, data: MarkerData, category?: number): void {
    if (data.icon) {
      if (typeof data.icon === "function") {
        marker.setIcon(data.icon(data, category));
      } else {
        marker.setIcon(data.icon);
      }
    }

    if (data.popup) {
      const content =
        typeof data.popup === "function"
          ? data.popup(data, category)
          : data.popup;
      if (marker.getPopup()) {
        marker.setPopupContent(content);
      } else {
        marker.bindPopup(content, data.popupOptions);
      }
    }
  }

  public onAdd(map: L.Map): this {
    this._map = map;
    map.on("movestart", this._moveStart, this);
    map.on("moveend", this._moveEnd, this);
    map.on("zoomend", this._zoomStart, this);
    map.on("zoomend", this._zoomEnd, this);
    this.ProcessView();
    map.addLayer(this.spiderfier as any);
    return this;
  }

  public onRemove(map: L.Map): this {
    map.off("movestart", this._moveStart, this);
    map.off("moveend", this._moveEnd, this);
    map.off("zoomend", this._zoomStart, this);
    map.off("zoomend", this._zoomEnd, this);

    for (let i = 0, l = this._objectsOnMap.length; i < l; ++i) {
      const marker = this._objectsOnMap[i].data._leafletMarker;
      if (marker) {
        map.removeLayer(marker as any);
      }
    }

    this._objectsOnMap = [];
    this.Cluster.ResetClusters();
    map.removeLayer(this.spiderfier as any);
    return this;
  }

  private _moveStart(): void {
    this._moveInProgress = true;
  }

  private _moveEnd(e: L.LeafletEvent & { hard?: boolean }): void {
    this._moveInProgress = false;
    this._hardMove = e.hard || false;
    this.ProcessView(true);
  }

  private _zoomStart(): void {
    this._zoomInProgress = true;
  }

  private _zoomEnd(): void {
    this._zoomInProgress = false;
    this.ProcessView();
  }

  private _addLabel(layer: L.Marker, id: number): void {
    // This is ugly but there is no getContainer method on the tooltip :(
    if (!layer.getTooltip()) {
      return;
    }
    const label = (layer.getTooltip() as any)?._source?._tooltip?._container as HTMLElement;
    if (label) {
      // We need the bounding rectangle of the label itself
      const rect = label.getBoundingClientRect();

      const boundingBox = {
        bottomLeft: [rect.left, rect.top] as [number, number],
        topRight: [rect.right, rect.bottom] as [number, number],
      };

      // Ingest the label into labelgun itself
      this._labelGun.ingestLabel(
        boundingBox,
        id.toString(),
        1, // Weight
        label,
        `Test ${id}`,
        false
      );
    }
  }

  public ProcessView(relabel?: boolean): void {
    if (!this._map || this._zoomInProgress || this._moveInProgress) {
      return;
    }

    const map = this._map;
    const bounds = map.getBounds();
    const zoom = Math.floor(map.getZoom());
    const marginRatio = this.clusterMargin / this.Cluster.Size;
    const resetIcons = this._resetIcons;

    const southWest = bounds.getSouthWest();
    const northEast = bounds.getNorthEast();
    const clusters = this.Cluster.ProcessView({
      minLat: southWest.lat,
      minLng: southWest.lng,
      maxLat: northEast.lat,
      maxLng: northEast.lng,
    });

    const objectsOnMap = this._objectsOnMap;
    const newObjectsOnMap: PruneCluster.Cluster[] = [];
    const markersOnMap = new Array(objectsOnMap.length);

    for (let i = 0, l = objectsOnMap.length; i < l; ++i) {
      const marker = objectsOnMap[i].data._leafletMarker;
      if (marker) {
        markersOnMap[i] = marker;
        (marker as any)._removeFromMap = true;
        if ((marker as any).getTooltip && (marker as any).getTooltip() && relabel) {
          const tooltipContainer = ((marker as any).getTooltip() as any)?._source?._tooltip?._container as HTMLElement;
          if (tooltipContainer) {
            tooltipContainer.style.opacity = "0";
          }
        }
      }
    }

    const clusterCreationList: PruneCluster.Cluster[] = [];
    const clusterCreationListPopOne: PruneCluster.Cluster[] = [];
    const opacityUpdateList: L.Marker[] = [];
    const workingList: PruneCluster.Cluster[] = [];

    for (let i = 0, l = clusters.length; i < l; ++i) {
      const icluster = clusters[i];
      const iclusterData = icluster.data;

      if (!icluster.bounds || !icluster.averagePosition) continue;

      const latMargin =
        (icluster.bounds.maxLat - icluster.bounds.minLat) * marginRatio;
      const lngMargin =
        (icluster.bounds.maxLng - icluster.bounds.minLng) * marginRatio;

      for (let j = 0, ll = workingList.length; j < ll; ++j) {
        const c = workingList[j];
        if (!c.bounds || !c.averagePosition) continue;

        // Evict only when c is too far left to ever merge again. The merge
        // test below compares average positions extended by lngMargin on each
        // side, so c stays a candidate while it is within 2*lngMargin of the
        // current cluster's cell. Evicting on raw bounds (the old check)
        // dropped candidates before the margin test whenever clusterMargin
        // exceeds the grid cell size (asset maps), which broke horizontal
        // merging and left cluster icons overlapping side by side.
        if (c.bounds.maxLng + 2 * lngMargin < icluster.bounds.minLng) {
          workingList.splice(j, 1);
          --j;
          --ll;
          continue;
        }

        const oldMaxLng = c.averagePosition.lng + lngMargin;
        const oldMinLat = c.averagePosition.lat - latMargin;
        const oldMaxLat = c.averagePosition.lat + latMargin;
        const newMinLng = icluster.averagePosition.lng - lngMargin;
        const newMinLat = icluster.averagePosition.lat - latMargin;
        const newMaxLat = icluster.averagePosition.lat + latMargin;

        if (
          oldMaxLng > newMinLng &&
          oldMaxLat > newMinLat &&
          oldMinLat < newMaxLat
        ) {
          iclusterData._leafletCollision = true;
          c.ApplyCluster(icluster);
          break;
        }
      }

      if (!iclusterData._leafletCollision) {
        workingList.push(icluster);
      }
    }

    clusters.forEach((cluster) => {
      let m: L.Marker | undefined;
      const data = cluster.data;

      if (data._leafletCollision) {
        data._leafletCollision = false;
        data._leafletOldPopulation = 0;
        data._leafletOldHashCode = 0;
        return;
      }

      if (!cluster.averagePosition) return;

      const position = new L.LatLng(
        cluster.averagePosition.lat,
        cluster.averagePosition.lng
      );

      const oldMarker = data._leafletMarker;
      if (oldMarker) {
        if (
          cluster.population === 1 &&
          data._leafletOldPopulation === 1 &&
          cluster.hashCode === (oldMarker as any)._hashCode
        ) {
          if (
            resetIcons ||
            (oldMarker as any)._zoomLevel !== zoom ||
            (cluster.lastMarker && cluster.lastMarker.data.forceIconRedraw)
          ) {
            if (cluster.lastMarker) {
              this.PrepareLeafletMarker(
                oldMarker as any,
                cluster.lastMarker.data,
                cluster.lastMarker.category
              );
              if (cluster.lastMarker.data.forceIconRedraw) {
                cluster.lastMarker.data.forceIconRedraw = false;
              }
            }
          }
          (oldMarker as any).setLatLng(position);
          m = oldMarker as any;
        } else if (
          (cluster.population || 0) > 1 &&
          ((data._leafletOldPopulation as number) || 0) > 1 &&
          ((oldMarker as any)._zoomLevel === zoom ||
            (data._leafletPosition && (data._leafletPosition as any).equals(position)))
        ) {
          (oldMarker as any).setLatLng(position);
          if (
            resetIcons ||
            cluster.population !== data._leafletOldPopulation ||
            cluster.hashCode !== data._leafletOldHashCode
          ) {
            const boundsCopy: Bounds = {} as Bounds;
            if (cluster.bounds) {
              Object.assign(boundsCopy, cluster.bounds);
            }
            (oldMarker as any)._leafletClusterBounds = boundsCopy;
            (oldMarker as any).setIcon(this.BuildLeafletClusterIcon(cluster));
          }
          data._leafletOldPopulation = cluster.population;
          data._leafletOldHashCode = cluster.hashCode;
          m = oldMarker as any;
        }
      }

      if (!m) {
        if (cluster.population === 1) {
          clusterCreationListPopOne.push(cluster);
        } else {
          clusterCreationList.push(cluster);
        }
        data._leafletPosition = position;
        data._leafletOldPopulation = cluster.population;
        data._leafletOldHashCode = cluster.hashCode;
      } else {
        (m as any)._removeFromMap = false;
        newObjectsOnMap.push(cluster);
        (m as any)._zoomLevel = zoom;
        (m as any)._hashCode = cluster.hashCode;
        (m as any)._population = cluster.population;
        data._leafletMarker = m;
        data._leafletPosition = position;
      }
    });

    const finalClusterCreationList = clusterCreationListPopOne.concat(clusterCreationList);

    for (let i = 0, l = objectsOnMap.length; i < l; ++i) {
      const icluster = objectsOnMap[i];
      const idata = icluster.data;
      const marker = idata._leafletMarker;

      if (marker && (marker as any)._removeFromMap) {
        let remove = true;
        if ((marker as any)._zoomLevel === zoom && icluster.averagePosition && icluster.bounds) {
          const pa = icluster.averagePosition;
          const latMargin =
            (icluster.bounds.maxLat - icluster.bounds.minLat) * marginRatio;
          const lngMargin =
            (icluster.bounds.maxLng - icluster.bounds.minLng) * marginRatio;

          for (let j = 0, ll = finalClusterCreationList.length; j < ll; ++j) {
            const jcluster = finalClusterCreationList[j];
            const jdata = jcluster.data;

            if (
              (marker as any)._population === 1 &&
              jcluster.population === 1 &&
              (marker as any)._hashCode === jcluster.hashCode
            ) {
              if (resetIcons || (jcluster.lastMarker && jcluster.lastMarker.data.forceIconRedraw)) {
                if (jcluster.lastMarker) {
                  this.PrepareLeafletMarker(
                    marker as any,
                    jcluster.lastMarker.data,
                    jcluster.lastMarker.category
                  );
                  if (jcluster.lastMarker.data.forceIconRedraw) {
                    jcluster.lastMarker.data.forceIconRedraw = false;
                  }
                }
              }
              if (jdata._leafletPosition) {
                (marker as any).setLatLng(jdata._leafletPosition);
              }
              remove = false;
            } else if (jcluster.averagePosition) {
              const pb = jcluster.averagePosition;
              const oldMinLng = pa.lng - lngMargin;
              const newMaxLng = pb.lng + lngMargin;
              const oldMaxLng = pa.lng + lngMargin;
              const oldMinLat = pa.lat - latMargin;
              const oldMaxLat = pa.lat + latMargin;
              const newMinLng = pb.lng - lngMargin;
              const newMinLat = pb.lat - latMargin;
              const newMaxLat = pb.lat + latMargin;

              if (
                ((marker as any)._population || 0) > 1 &&
                (jcluster.population || 0) > 1 &&
                oldMaxLng > newMinLng &&
                oldMinLng < newMaxLng &&
                oldMaxLat > newMinLat &&
                oldMinLat < newMaxLat
              ) {
                if (jdata._leafletPosition) {
                  (marker as any).setLatLng(jdata._leafletPosition);
                }
                (marker as any).setIcon(this.BuildLeafletClusterIcon(jcluster));
                const poisson: Bounds = {} as Bounds;
                if (jcluster.bounds) {
                  Object.assign(poisson, jcluster.bounds);
                }
                (marker as any)._leafletClusterBounds = poisson;
                jdata._leafletOldPopulation = jcluster.population;
                jdata._leafletOldHashCode = jcluster.hashCode;
                (marker as any)._population = jcluster.population;
                remove = false;
              }
            }

            if (!remove) {
              jdata._leafletMarker = marker;
              (marker as any)._removeFromMap = false;
              newObjectsOnMap.push(jcluster);
              finalClusterCreationList.splice(j, 1);
              --j;
              --ll;
              break;
            }
          }
        }

        if (remove && !(marker as any)._removeFromMap) {
          console.error("wtf");
        }
      }
    }

    for (let i = 0, l = finalClusterCreationList.length; i < l; ++i) {
      const icluster = finalClusterCreationList[i];
      const idata = icluster.data;
      const iposition = idata._leafletPosition;

      if (!iposition) continue;

      let creationMarker: L.Marker;
      if (icluster.population === 1 && icluster.lastMarker) {
        creationMarker = this.BuildLeafletMarker(icluster.lastMarker, iposition as any);
      } else {
        creationMarker = this.BuildLeafletCluster(icluster, iposition as any);
      }

      creationMarker.addTo(map);
      creationMarker.setOpacity(0);
      opacityUpdateList.push(creationMarker);
      idata._leafletMarker = creationMarker;
      (creationMarker as any)._zoomLevel = zoom;
      (creationMarker as any)._hashCode = icluster.hashCode;
      (creationMarker as any)._population = icluster.population;
      newObjectsOnMap.push(icluster);
    }

    window.setTimeout(() => {
      for (let i = 0, l = opacityUpdateList.length; i < l; ++i) {
        const m = opacityUpdateList[i];
        const icon = (m as any)._icon as HTMLElement;
        const shadow = (m as any)._shadow as HTMLElement;
        if (icon) L.DomUtil.addClass(icon, "prunecluster-anim");
        if (shadow) L.DomUtil.addClass(shadow, "prunecluster-anim");
        m.setOpacity(1);
      }
    }, 1);

    if (this._hardMove) {
      for (let i = 0, l = markersOnMap.length; i < l; ++i) {
        const marker = markersOnMap[i];
        if (marker && (marker as any)._removeFromMap) {
          map.removeLayer(marker as any);
        }
      }
    } else {
      if (this._removeTimeoutId !== 0) {
        window.clearTimeout(this._removeTimeoutId);
        for (let i = 0, l = this._markersRemoveListTimeout.length; i < l; ++i) {
          map.removeLayer(this._markersRemoveListTimeout[i]);
        }
      }

      const toRemove: L.Marker[] = [];
      for (let i = 0, l = markersOnMap.length; i < l; ++i) {
        const marker = markersOnMap[i];
        if (marker && (marker as any)._removeFromMap) {
          marker.setOpacity(0);
          toRemove.push(marker);
        }
      }

      if (toRemove.length > 0) {
        this._removeTimeoutId = window.setTimeout(() => {
          for (let i = 0, l = toRemove.length; i < l; ++i) {
            map.removeLayer(toRemove[i] as any);
          }
          this._removeTimeoutId = 0;
        }, 300);
      }
      this._markersRemoveListTimeout = toRemove;
    }

    if (relabel) {
      this._labelGun.reset();
      for (let i = 0, l = newObjectsOnMap.length; i < l; ++i) {
        const marker = newObjectsOnMap[i].data._leafletMarker;
        if (marker) {
          this._addLabel(marker as any, (marker as any)._hashCode || 0);
        }
      }
    }

    this._labelGun.update();
    this._objectsOnMap = newObjectsOnMap;
    this._hardMove = false;
    this._resetIcons = false;
  }

  public FitBounds(withFiltered: boolean = true): void {
    const bounds = this.Cluster.ComputeGlobalBounds(withFiltered);
    if (bounds) {
      this._map.fitBounds(
        new L.LatLngBounds(
          new L.LatLng(bounds.minLat, bounds.maxLng),
          new L.LatLng(bounds.maxLat, bounds.minLng)
        )
      );
    }
  }

  public GetMarkers(): PruneCluster.Marker[] {
    return this.Cluster.GetMarkers();
  }

  public RedrawIcons(processView: boolean = true): void {
    this._resetIcons = true;
    if (processView) {
      this.ProcessView();
    }
  }
}

class PruneClusterLeafletSpiderfier extends L.Layer {
  private readonly _2PI = Math.PI * 2;
  private readonly _circleFootSeparation = 25;
  private readonly _circleStartAngle = Math.PI / 6;
  private readonly _spiralFootSeparation = 28;
  private readonly _spiralLengthStart = 11;
  private readonly _spiralLengthFactor = 5;
  private readonly _spiralCountTrigger = 8;
  public spiderfyDistanceMultiplier = 1;

  private _cluster: PruneClusterForLeaflet;
  private _currentMarkers: L.Marker[] = [];
  private _multiLines: boolean;
  private _lines: L.Polyline | L.Polyline[];
  private _currentCenter?: L.LatLng;
  private _clusterMarker?: L.Marker;
  protected _map!: L.Map;

  constructor(cluster: PruneClusterForLeaflet) {
    super();
    this._cluster = cluster;
    this._currentMarkers = [];
    this._multiLines = !!(L as any).multiPolyline;
    this._lines = this._multiLines
      ? (L as any).multiPolyline([], { weight: 1.5, color: "#222" })
      : L.polyline([], { weight: 1.5, color: "#222" });
  }

  public onAdd(map: L.Map): this {
    this._map = map;
    this._map.on("overlappingmarkers", this.Spiderfy as any, this);
    this._map.on("click", this.Unspiderfy, this);
    this._map.on("zoomend", this.Unspiderfy, this);
    return this;
  }

  public Spiderfy = (data: OverlappingMarkersEvent): void => {
    if (data.cluster !== this._cluster) {
      return;
    }

    this.Unspiderfy();

    const markers = data.markers.filter((marker) => !marker.filtered);
    this._currentCenter = data.center;
    const centerPoint = this._map.latLngToLayerPoint(data.center);

    let points: L.Point[];
    if (markers.length >= this._spiralCountTrigger) {
      points = this._generatePointsSpiral(markers.length, centerPoint);
    } else {
      if (this._multiLines) {
        centerPoint.y += 10;
      }
      points = this._generatePointsCircle(markers.length, centerPoint);
    }

    let polylines: L.LatLng[][] = [];
    const leafletMarkers: L.Marker[] = [];
    const projectedPoints: L.LatLng[] = [];

    for (let i = 0, l = points.length; i < l; ++i) {
      const pos = this._map.layerPointToLatLng(points[i]);
      const m = this._cluster.BuildLeafletMarker(markers[i], data.center);
      m.setZIndexOffset(SPIDERFY_Z_INDEX_OFFSET);
      m.setOpacity(0);
      this._currentMarkers.push(m);
      this._map.addLayer(m);
      leafletMarkers.push(m);
      projectedPoints.push(pos);
    }

    window.setTimeout(() => {
      for (let i = 0, l = points.length; i < l; ++i) {
        leafletMarkers[i].setLatLng(projectedPoints[i]).setOpacity(1);
      }

      const startTime = +new Date();
      const interval = 42;
      const duration = 290;

      const anim = window.setInterval(() => {
        polylines = [];
        const now = +new Date();
        const d = now - startTime;
        let stepRatio: number;

        if (d >= duration) {
          window.clearInterval(anim);
          stepRatio = 1.0;
        } else {
          stepRatio = d / duration;
        }

        const center = data.center;
        for (let i = 0, l = points.length; i < l; ++i) {
          const p = projectedPoints[i];
          const diffLat = p.lat - center.lat;
          const diffLng = p.lng - center.lng;
          polylines.push([
            center,
            new L.LatLng(
              center.lat + diffLat * stepRatio,
              center.lng + diffLng * stepRatio
            ),
          ]);
        }
        (this._lines as any).setLatLngs(polylines);
      }, interval);
    }, 1);

    (this._lines as any).setLatLngs(polylines);
    this._map.addLayer(this._lines as L.Layer);

    if (data.marker) {
      this._clusterMarker = data.marker.setOpacity(0.3);
    }
  };

  private _generatePointsCircle(count: number, centerPt: L.Point): L.Point[] {
    const circumference =
      this.spiderfyDistanceMultiplier *
      this._circleFootSeparation *
      (2 + count);
    const legLength = circumference / this._2PI;
    const angleStep = this._2PI / count;
    const res: L.Point[] = [];

    res.length = count;
    for (let i = count - 1; i >= 0; i--) {
      const angle = this._circleStartAngle + i * angleStep;
      res[i] = new L.Point(
        Math.round(centerPt.x + legLength * Math.cos(angle)),
        Math.round(centerPt.y + legLength * Math.sin(angle))
      );
    }
    return res;
  }

  private _generatePointsSpiral(count: number, centerPt: L.Point): L.Point[] {
    let legLength = this.spiderfyDistanceMultiplier * this._spiralLengthStart;
    const separation = this.spiderfyDistanceMultiplier * this._spiralFootSeparation;
    const lengthFactor = this.spiderfyDistanceMultiplier * this._spiralLengthFactor;
    let angle = 0;
    const res: L.Point[] = [];

    res.length = count;
    for (let i = count - 1; i >= 0; i--) {
      angle += separation / legLength + i * 0.0005;
      res[i] = new L.Point(
        Math.round(centerPt.x + legLength * Math.cos(angle)),
        Math.round(centerPt.y + legLength * Math.sin(angle))
      );
      legLength += (this._2PI * lengthFactor) / angle;
    }
    return res;
  }

  public Unspiderfy = (): void => {
    if (this._currentCenter) {
      for (let i = 0, l = this._currentMarkers.length; i < l; ++i) {
        this._currentMarkers[i].setLatLng(this._currentCenter).setOpacity(0);
      }
    }

    const markers = this._currentMarkers;
    window.setTimeout(() => {
      for (let i = 0, l = markers.length; i < l; ++i) {
        this._map.removeLayer(markers[i]);
      }
    }, 300);

    this._currentMarkers = [];
    this._map.removeLayer(this._lines as L.Layer);

    if (this._clusterMarker) {
      this._clusterMarker.setOpacity(1);
    }
  };

  public onRemove(map: L.Map): this {
    this.Unspiderfy();
    map.off("overlappingmarkers", this.Spiderfy as any, this);
    map.off("click", this.Unspiderfy, this);
    map.off("zoomend", this.Unspiderfy, this);
    return this;
  }
}

export { PruneClusterForLeaflet, PruneCluster };